import { useRef, useState } from "react";
import { BlockBlobClient } from "@azure/storage-blob";
import useFetch from "hooks/useFetch";
import { getFetchOptions } from "utils/fetch.js";

export const useContentUpload = () => {
	const { execute } = useFetch();
	const [uploadFilename, setUploadFilename] = useState("");
	const [uploadProgressValue, setUploadProgressValue] = useState(0);
	const cancelUploadRef = useRef(null);
	const currentJobId = useRef(null);

	/**
	 * BlockBlobClient.uploadData only fires the onProgress on completion
	 * BlockBlobClient.uploadStream (which does fires onProgress correctly) is node.js runtime only
	 * BlockBlobClient.uploadFile requires a file name, instead of the stream we have
	 * ...therefore and complicated chunking process is required.
	 */
	const uploadWithProgress = async (file, blobSasUrl) => {
		const blobClient = new BlockBlobClient(blobSasUrl);
		cancelUploadRef.current = new AbortController();

		const fileSize = file.size;
		const blockSize = 4 * 1024 * 1024; // 4 MB blocks
		let uploadedBytes = 0;

		try {
			const arrayBuffer = await file.arrayBuffer();

			const blockIds = [];
			const totalBlocks = Math.ceil(fileSize / blockSize);

			// Upload blocks.
			for (let i = 0; i < totalBlocks; ++i) {
				if (cancelUploadRef.current?.signal.aborted) {
					console.debug("File upload cancelled");
					return false;
				}

				const start = i * blockSize;
				const end = Math.min(start + blockSize, fileSize);
				const blockId = btoa(`block-${i.toString().padStart(6, '0')}`);
				const blockData = arrayBuffer.slice(start, end);

				await blobClient.stageBlock(blockId, blockData, blockData.byteLength, {
					abortSignal: cancelUploadRef.current.signal
				});

				blockIds.push(blockId);
				uploadedBytes = end;

				// Update progress.
				const percent = Math.round((uploadedBytes / fileSize) * 100);
				setUploadProgressValue(percent);
			}

			// Commit the blocks.
			await blobClient.commitBlockList(blockIds, {
				abortSignal: cancelUploadRef.current.signal
			});

			setUploadProgressValue(100);
			return true;
		}
		catch (error) {
			if (error.name === "AbortError") {
				console.debug("Upload was cancelled by user");
			}
			else {
				console.error("Upload error:", error);
			}
			return false;
		}
	};

	const handleErrorCallback = (onError, message) => {
		if (onError && typeof onError === "function") {
			onError(message);
		}
	}

	const uploadZipFile = async (existingDraft, file, onUploadSuccess, onValidationSuccess, onError) => {
		if (existingDraft) {
			const deleteOptions = getFetchOptions({
				method: "DELETE",
				body: JSON.stringify(existingDraft)
			});
			execute("/api/v3/Upload/Zip/DeletePreviousDraft", deleteOptions, (deleteData, deleteError) => {
				if (deleteError) {
					console.warn("Failed to cleanup previous file:", existingDraft, deleteError);
				}
			});
		}

		if (!file) {
			handleErrorCallback(onError, "Missing file");
			return;
		}

		setUploadProgressValue(0);
		setUploadFilename(file.name);

		const sasTokenOptions = getFetchOptions({
			method: "POST",
			body: JSON.stringify(file.name)
		});

		execute("/api/v3/Upload/Zip/GetSasToken", sasTokenOptions, async (sasTokenData, sasTokenError) => {
			if (!sasTokenError) {
				const { BlobSasUrl, JobId, OriginalFileName } = sasTokenData;
				currentJobId.current = JobId;
				let uploadSuccess = await uploadWithProgress(file, BlobSasUrl);
				if (uploadSuccess) {
					if (onUploadSuccess && typeof onUploadSuccess === "function") {
						onUploadSuccess();
					}
				}
				else {
					handleErrorCallback(onError, "Upload was not completed.");
					return;
				}

				const startJobOptions = getFetchOptions({
					method: "POST",
					body: JSON.stringify(OriginalFileName)
				});
				execute(`/api/v3/Upload/Zip/ProcessFile/${JobId}`, startJobOptions, (processData, processError) => {
					if (!processError) {
						if (onValidationSuccess && typeof onValidationSuccess === "function") {
							onValidationSuccess(processData, JobId);
						}
					}
					else if (onError && typeof onError === "function") {
						console.error("File Processing failed", JobId, processError);
						onError(processError.Message);
					}
				});
			}
			else {
				handleErrorCallback(onError, sasTokenError.Message);
			}
		});
	};

	const cancelUpload = () => {
		if (cancelUploadRef.current) {
			cancelUploadRef.current.abort();
		}
		if (currentJobId.current) {
			const cancelOptions = getFetchOptions({
				method: "DELETE",
				body: JSON.stringify(uploadFilename)
			});
			execute(`/api/v3/Upload/Zip/Cancel/${currentJobId.current}`, cancelOptions, (cancelData, cancelError) => {
				if (cancelError) {
					console.warn("Failed to cleanup temporary file:", cancelError);
				}
			});
		}
		clearProgress();
	};

	const clearProgress = () => {
		setUploadProgressValue(0);
		setUploadFilename("");
		currentJobId.current = null;
	};

	return {
		uploadZipFile,
		cancelUpload,
		uploadFilename,
		uploadProgressValue,
		clearProgress
	};
};